import type { QueueEntry } from '../../offline/mutationQueue.ts';
import type { Item } from './itemsApi.ts';
import { optimisticItem } from './useItems.ts';

/**
 * The list as it actually stands: what the server last said, plus everything queued since.
 *
 * Without this the queue is invisible to the screen the moment the page is reloaded. An
 * optimistic row lives in React Query's cache, which is memory only — so a pull-to-refresh
 * in a shop threw it away, `/api/lists/{id}/items` was answered by the service worker from
 * the snapshot cached *before* the add, and an item that was safely queued simply vanished.
 * The natural response is to add it again, and since every add mints its own UUIDv7 the
 * two are genuinely different items: somebody adding celery three times through three
 * refreshes got three celeries when the queue finally drained. The queue was right every
 * time; only the drawing was wrong.
 *
 * It is a *view*, computed on render and never written back into the query cache. A cache
 * that had pending changes baked into it could not tell them apart from confirmed ones,
 * and the flush would have nothing to correct against.
 *
 * The intents are replayed in queue order, which is the order they will be sent in — so
 * what is on screen offline is what the server will be told, in the order it will hear it.
 */
export function applyPending(
  items: Item[],
  entries: QueueEntry[],
  listId: string,
  who: { currentUserId: string | null; assignNewTo: string | null },
): Item[] {
  if (entries.length === 0) {
    return items;
  }

  let result = items;

  for (const entry of entries) {
    if (entry.mutation.listId === listId) {
      result = apply(result, entry, who);
    }
  }

  return result;
}

function apply(
  items: Item[],
  entry: QueueEntry,
  who: { currentUserId: string | null; assignNewTo: string | null },
): Item[] {
  const { mutation } = entry;
  const at = new Date(entry.queuedAt).toISOString();

  switch (mutation.kind) {
    case 'add':
      // Skip when the row is already there: the add may have reached the server and be
      // waiting only for its entry to be removed. Ids are minted by the client precisely
      // so that this comparison is possible.
      return items.some((item) => item.id === mutation.itemId)
        ? items
        : [
            ...items,
            optimisticItem({
              id: mutation.itemId,
              name: mutation.name,
              note: mutation.note,
              listId: mutation.listId,
              assignTo: who.assignNewTo,
              at,
            }),
          ];

    case 'check':
      return items.map((item) =>
        item.id === mutation.itemId
          ? {
              ...item,
              isChecked: true,
              // Only claim the tick if nobody holds it. The server keeps the first
              // checker's attribution, and drawing ourselves over it here would show a
              // colour that is about to change back.
              checkedByUserId: item.checkedByUserId ?? who.currentUserId,
              checkedAt: item.checkedAt ?? at,
            }
          : item,
      );

    case 'uncheck':
      return items.map((item) =>
        item.id === mutation.itemId
          ? { ...item, isChecked: false, checkedByUserId: null, checkedAt: null }
          : item,
      );

    case 'update':
      return items.map((item) =>
        item.id === mutation.itemId
          ? {
              ...item,
              name: mutation.name,
              note: mutation.note ?? null,
              assignedToUserId: mutation.assignedToUserId ?? null,
            }
          : item,
      );

    case 'delete':
      return items.filter((item) => item.id !== mutation.itemId);

    case 'clearChecked':
      return items.filter((item) => !item.isChecked);

    // Photos and aisles are filed against the *name*, in catalogues of their own, and
    // neither is a field on the row — so there is nothing here for them to change. They
    // are listed rather than defaulted so that adding a new kind of intent is a compile
    // error until somebody has decided which of the two it is.
    case 'attachImage':
    case 'removeImage':
    case 'setAisle':
    case 'clearAisle':
      return items;
  }
}
